import React, { FC, PropsWithChildren, useState } from 'react';
import { PermissionAccess } from 'providence-types';
import { HttpService } from '@core/services';
import { PermittingHttpService } from '@core/services/http';
import { canBeEditable } from '@core/utils/methods';
import { JurisdictionDetailsPermittingType } from '@models/interfaces';
import { CustomTable } from '../../components/custom-table';
import { DeleteModal } from '../../components/delete-modal';
import { PrimaryButton } from '../../modules/ui-kit/primary-button';
import { getPermitTypeTableColumns } from './helpers';
import { PermitTypeTableModal } from './modals';
import { DEFAULT_VALUES, PermitTypeTableProps } from './models';

import './styles.scss';

export const PermitTypeTable: FC<PermitTypeTableProps> = ({
  isEditing,
  data,
  permittingId,
  tableAccess = PermissionAccess.ReadOnly,
  reqType,
}: PropsWithChildren<PermitTypeTableProps>) => {
  const [formValue, setFormValue] =
    useState<JurisdictionDetailsPermittingType>(DEFAULT_VALUES);
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);

  const openAddModal = (): void => {
    setFormValue(DEFAULT_VALUES);
    setVisibleModal(!visibleModal);
  };

  const selectRowForEdit = (
    permittingType: JurisdictionDetailsPermittingType,
  ): void => {
    setFormValue(permittingType);
    setVisibleModal(!visibleModal);
  };

  const selectRowForDelete = (
    permittingType: JurisdictionDetailsPermittingType,
  ): void => {
    setFormValue(permittingType);
    setDeleteModalVisible(!deleteModalVisible);
  };

  const onDelete = async (): Promise<void> => {
    await HttpService.getHttpRequests(PermittingHttpService)[
      reqType === 'permitting'
        ? 'permittingRemovePermittingType'
        : 'jurisdictionRemovePermittingType'
    ]({ permittingId, permittingTypeId: formValue.id });
    setDeleteModalVisible(!deleteModalVisible);
  };

  return (
    <div className="permit-type-table">
      <CustomTable
        columns={getPermitTypeTableColumns(
          selectRowForEdit,
          selectRowForDelete,
          !!permittingId && canBeEditable(tableAccess, isEditing),
        )}
        dataSource={data}
        pagination={false}
        hoverable={false}
      />
      <PermitTypeTableModal
        permittingId={permittingId}
        visibleModal={visibleModal}
        toggleModal={(): void => setVisibleModal(!visibleModal)}
        formValue={formValue}
        reqType={reqType}
      />
      <DeleteModal
        onOk={onDelete}
        onCancel={(): void => setDeleteModalVisible(!deleteModalVisible)}
        visible={deleteModalVisible}
      />
      {!!permittingId && canBeEditable(tableAccess, isEditing) && (
        <PrimaryButton
          icon="+"
          title="Add Type"
          className="permit-type-table__add-button"
          onClick={openAddModal}
        />
      )}
    </div>
  );
};
